export const SAML_CALLBACK_SCHEME = 'com.fivefeedback.coss';
export const SAML_CALLBACK_HOST = 'saml';
export const SAML_REDIRECT_URI = `${SAML_CALLBACK_SCHEME}://${SAML_CALLBACK_HOST}`;

export interface SamlExchangeResponse {
    access_token: string;
    token_type: string;
    expires_in: number;
    user_id: number;
}

export type SamlLoginFailure = 'invalid' | 'network' | 'unavailable' | 'cancelled';

/**
 * Preprod returns status as the strings "true" and "false". A boolean true is
 * accepted as well. Anything else, including "false", stays on classic login.
 */
export function shouldStartSaml(status: boolean | string | null | undefined, link?: string | null): boolean {
    return (status === true || status === 'true') && !!link;
}

export function isSamlCallbackUrl(url: string): boolean {
    const parsed = parseUrl(url);
    if (!parsed) {
        return false;
    }
    const scheme = parsed.protocol.replace(/:$/, '').toLowerCase();
    const path = parsed.pathname;
    return scheme === SAML_CALLBACK_SCHEME
        && parsed.hostname.toLowerCase() === SAML_CALLBACK_HOST
        && (path === '' || path === '/');
}

export function readSamlCode(url: string): string | null {
    if (!isSamlCallbackUrl(url)) {
        return null;
    }
    const code = new URL(url).searchParams.get('code')?.trim() ?? '';
    return code.length > 0 ? code : null;
}

/** Mock IdP errors: `cancelled` (Annuler) and `failed` (Échec). */
export function readSamlError(url: string): string | null {
    if (!isSamlCallbackUrl(url)) {
        return null;
    }
    const error = new URL(url).searchParams.get('error')?.trim() ?? '';
    return error.length > 0 ? error : null;
}

export function failureFromSamlCallback(error: string): SamlLoginFailure {
    return /cancel|annul/i.test(error) ? 'cancelled' : 'invalid';
}

export function classifySamlError(error: unknown): SamlLoginFailure {
    const status = readStatus(error);
    if (status === 400) {
        return 'invalid';
    }
    if (status === 0 || status === null) {
        return 'network';
    }
    return 'unavailable';
}

export function isSamlCancellation(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
        return false;
    }
    const record = error as {message?: unknown; errorMessage?: unknown};
    const message = `${String(record.message ?? '')} ${String(record.errorMessage ?? '')}`;
    // iOS reports ASWebAuthenticationSessionError.canceledLogin as error 1.
    return /cancel|annul/i.test(message) || /\berror 1\b/.test(message);
}

function parseUrl(url: string): URL | null {
    try {
        return new URL(url);
    } catch {
        return null;
    }
}

function readStatus(error: unknown): number | null {
    if (!error || typeof error !== 'object' || !('status' in error)) {
        return null;
    }
    const status = Number((error as {status: unknown}).status);
    return Number.isFinite(status) ? status : null;
}
